import { individualVatEvidenceFields } from '../../pages/EInvoice/VAT/VatEvidence/utils/VatEvidenceTypeFields';
import { IOrganization } from '../IOrganization';
import { ISelectData } from '../ISelect';
import { IDocument, IDocumentType } from '../einvoice/Document/IDocument';
import { ISendStatus } from '../einvoice/Document/Send/IDocumentSend';
import { ILegalEntity } from '../einvoice/LegalEntity/ILegalEntity';

export interface IVatEvidenceGroupNew {
  uuid?: string;
  income?: boolean;
  locked?: boolean;
  name?: string;
  number?: string;
  type?: 'INDIVIDUAL' | 'COLLECTIVE';
  onHold?: boolean;
  inputDate?: string;
}

export interface IVatEvidenceGroup extends IVatEvidenceGroupNew {
  uuid: string;
  name: string;
  number: string;
}

export interface IVatEvidenceGroupSearch {
  nameLike?: string;
  numberLike?: string;
  timeRange?: string;
  income?: boolean;
  type?: 'INDIVIDUAL' | 'COLLECTIVE';
  sort?: string;
}

export interface IVatEvidenceGenerate {
  vatEvidenceGroupUuid?: string;
  sendStatuses?: ISendStatus[];
  legalEntityUuid?: string;
  legalEntityObj?: ISelectData;
  issueDateFrom?: string;
  issueDateTo?: string;
  timeRange?: TaxPeriodRange;
  vatEvidencePeriod?: string;
  vatEvidenceNumber?: string;
}

export const DocumentSelectionBases = ['ADVANCE_PAYMENT', 'TRAFFIC'] as const;
export type IDocumentSelectionBase = typeof DocumentSelectionBases[number];

export interface IVatEvidenceBase {
  uuid?: string;
  number?: string;
  name?: string;
  timeRange?: TaxPeriodRange;
  accountNumber?: string;
  group?: IVatEvidenceGroupNew;
  type?: 'INDIVIDUAL' | 'COLLECTIVE';
  status?: VatStatus;
  invoiceNumber?: string;
  income?: boolean;
  legalEntity?: ILegalEntity;
  documentType?: IDocumentType;
  paymentDate?: string;
  amount?: string;
  organization?: IOrganization;
  vatReduction?: string;
  vatIncrease?: string;
  period?: string;
  evidenceDate?: string;
  documentNumber?: string;
  advancePaymentBasis?: string;
  advanceInvoiceNumbers?: string[];
  advanceInvoiceNumber?: string;
  year?: number;
  relatedPartyIdentifier?: string;
  invoice?: IDocument;
  documentSelectionBase?: IDocumentSelectionBase;
  errorMessage?: string;
  prepaymentDate?: string;
  vatRegistrationCode?: string;
  relatedInvoiceOption?: IRelatedInvoiceOption;
  relatedInternalInvoiceOption?: IRelatedInternalInvoiceOption;
  internalInvoiceNumber?: string;
  relatedVatRecordOutOfSystems?: string[];
  relatedVatRecordOutOfSystem?: string;
  relatedInvoiceIssueDateFrom?: string;
  relatedInvoiceIssueDateTo?: string;
  internalInvoiceOption?: IInternalInvoiceOption;
}

export interface IVatEvidence extends IVatEvidenceBase {
  items: IVatEvidenceGroupItem[];
  turnovers?: IVatEvidenceTurnover[];
}

export interface IVatEvidenceForm extends Partial<IVatEvidenceBase> {
  items?: {
    [key in IVatEvidenceType]?: Partial<IVatEvidenceGroupItem>;
  };
  turnovers?: {
    [key in IVatEvidenceType]?: Partial<IVatEvidenceTurnover>;
  };
}

export type IndividualVatEvidenceKeys = keyof typeof individualVatEvidenceFields;
export interface IVatEvidenceIndividualForm extends Partial<IVatEvidenceBase> {
  items?: {
    [key in IndividualVatEvidenceKeys]?: Partial<IVatEvidenceGroupItem>;
  };
}

export interface IVatEvidenceGroupItem {
  uuid?: string;
  type?: IVatEvidenceType;
  higherVatDescription?: string;
  higherVatTaxBase?: number | string;
  higherVatTaxAmount?: number | string;
  higherVatAmount?: number | string;
  lowerVatDescription?: string;
  lowerVatTaxBase?: number | string;
  lowerVatTaxAmount?: number | string;
  lowerVatAmount?: number | string;
  vatAmount?: number | string;
  amount?: number | string;
  higherAdvancePaymentVatTaxBase?: number | string;
  higherAdvancePaymentVatTaxAmount?: number | string;
  lowerAdvancePaymentVatTaxBase?: number | string;
  lowerAdvancePaymentVatTaxAmount?: number | string;
  higherAdvancePaymentVatDescription?: string;
  lowerAdvancePaymentVatDescription?: string;
  lowerRealEstateVatDescription?: string;
  lowerRealEstateVatTaxBase?: number | string;
  lowerRealEstateVatTaxAmount?: number | string;
  higherRealEstateVatDescription?: string;
  higherRealEstateVatTaxBase?: number | string;
  higherRealEstateVatTaxAmount?: number | string;
  lowerNoChargeRealEstateVatDescription?: string;
  lowerNoChargeRealEstateVatTaxBase?: number | string;
  lowerNoChargeRealEstateVatTaxAmount?: number | string;
  higherNoChargeRealEstateVatDescription?: string;
  higherNoChargeRealEstateVatTaxBase?: number | string;
  higherNoChargeRealEstateVatTaxAmount?: number | string;
}

export interface IVatEvidenceTurnover {
  uuid?: string;
  type?: IVatEvidenceType;
  higherIncreaseBaseAmount?: number | string;
  higherIncreaseVat?: number | string;
  lowerIncreaseBaseAmount?: number | string;
  lowerIncreaseVat?: number | string;
  higherReductionBaseAmount?: number | string;
  higherReductionVat?: number | string;
  lowerReductionBaseAmount?: number | string;
  lowerReductionVat?: number | string;
  higherPrepaymentReductionBaseAmount?: number | string;
  higherPrepaymentReductionVat?: number | string;
  lowerPrepaymentReductionBaseAmount?: number | string;
  lowerPrepaymentReductionVat?: number | string;
}

export interface IVatEvidenceTransformedData {
  uuid?: string;
  number?: string;
  name?: string;
  timeRange?: TaxPeriodRange;
  accountNumber?: string;
  group?: IVatEvidenceGroupNew;
  type?: 'INDIVIDUAL' | 'COLLECTIVE';
  status?: VatStatus;
  invoiceNumber?: string;
  income?: boolean;
  legalEntity?: ILegalEntity;
  documentType?: IDocumentType;
  paymentDate?: string;
  amount?: string;
  organization?: IOrganization;
  vatReduction?: string;
  vatIncrease?: string;
  period?: string;
  evidenceDate?: string;
  documentNumber?: string;
  advancePaymentBasis?: string;
  advanceInvoiceNumbers?: string[];
  advanceInvoiceNumber?: string;
  year?: number;
  relatedPartyIdentifier?: string;
  invoice?: IDocument;
  totalAmount?: number;
  documentSelectionBase?: IDocumentSelectionBase;
  errorMessage?: string;
  items?: {
    [key in IVatEvidenceType]: IVatEvidenceGroupItem;
  };
}

export interface IVatEvidenceSearch {
  vatEvidenceGroupUuid?: string;
  accountNumberLike?: string;
  timeRange?: string;
  status?: string;
  sefInvoiceType?: string;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  sort?: string;
  amount?: string;
  documentTypeUuid?: string;
  oldStructure?: boolean;
}

export type XlsResponse = {
  rowNumber: number;
  error: string;
  errorCode: string;
  errorMessage: string;
};

export type VatEvidenceGroupSort =
  | 'ID_ASC'
  | 'ID_DESC'
  | 'SEF_INVOICE_TYPE_ASC'
  | 'SEF_INVOICE_TYPE_DESC'
  | 'STATUS_ASC'
  | 'STATUS_DESC'
  | 'TIME_RANGE_ASC'
  | 'TIME_RANGE_DESC';

export const SefInvoiceTypes = [
  'COMMERCIAL_INVOICE',
  'CREDIT_NOTE',
  'DEBIT_NOTE',
  'PREPAYMENT_INVOICE',
  'INTERNAL_ACCOUNT_FOR_TURNOVER_OF_FOREIGNER',
  'OTHER_INTERNAL_STATEMENT',
] as const;
export type SefInvoiceType = typeof SefInvoiceTypes[number];

export const InternalInvoiceOptions = [
  'TURNOVER',
  'PREPAYMENT',
  'INCREASE',
  'REDUCTION',
] as const;
export type IInternalInvoiceOption = typeof InternalInvoiceOptions[number];

export const RelatedInvoiceOptions = ['INVOICE', 'PERIOD', 'PREPAYMENT_INVOICE'] as const;
export type IRelatedInvoiceOption = typeof RelatedInvoiceOptions[number];

export const RelatedInternalInvoiceOptions = [
  'INTERNAL_INVOICE_FOR_TURNOVER',
  'INTERNAL_INVOICE_FOR_PREPAYMENT',
] as const;
export type IRelatedInternalInvoiceOption = typeof RelatedInternalInvoiceOptions[number];

export const VatStatuses = [
  'PENDING',
  'RECORDED',
  'REPLACED',
  'CANCELLED',
  'QUEUED',
  'ERROR',
] as const;
export type VatStatus = typeof VatStatuses[number];

export const CollectiveVatEvidenceTypes = [
  'COLLECTIVE',
  'COLLECTIVE_ADVANCE_PAYMENT',
  'COLLECTIVE_WITH_COMPENSATION',
  'COLLECTIVE_WITHOUT_COMPENSATION',
  'COLLECTIVE_TOURIST_SERVICES',
  'COLLECTIVE_COLLECTIBLES_AND_ARTS',
  'SUPPLIER_TAX_DEBTOR_TURNOVER',
  'RECEIVER_TAX_DEBTOR_TURNOVER',
  'STORNO_SUPPLIER_TAX_DEBTOR_TURNOVER',
  'STORNO_RECEIVER_TAX_DEBTOR_TURNOVER',
] as const;
export type CollectiveVatEvidenceType = typeof CollectiveVatEvidenceTypes[number];

export type IVatEvidenceType =
  | 'INDIVIDUAL'
  | 'INDIVIDUAL_INBOUND'
  | 'INDIVIDUAL_OUTBOUND'
  | CollectiveVatEvidenceType;

export const TaxPeriodRanges = ['MONTHLY', 'QUARTERLY'] as const;
export type TaxPeriodRange = typeof TaxPeriodRanges[number];

export const DocumentTypes = ['INDIVIDUAL', 'COLLECTIVE'] as const;

export const Months = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER',
];
export type Month = typeof Months[number];

export const Quarters = [
  'FIRST_QUARTER',
  'SECOND_QUARTER',
  'THIRD_QUARTER',
  'FOURTH_QUARTER',
];
export type Quarter = typeof Quarters[number];

export type VatEvidenceFetchOptions =
  | 'GROUP'
  | 'ITEMS'
  | 'LEGAL_ENTITY'
  | 'INVOICE'
  | 'DOCUMENT_TYPE';
